import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  registerReloadWarning,
  unregisterReloadWarning,
} from 'hooks/useReloadWarning';
import { compact, get, groupBy, keyBy, merge } from 'lodash';
import {
  type StartAppListenerEffect,
  type StartAppListeningPredicate,
  startAppListening,
} from 'slices/listenerMiddleware';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultAPIState } from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import { validateObjectId } from 'utils/objectId.validation';
import {
  addEntityToState,
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  upsertEntity,
} from 'utils/sliceHelpers';
import {
  type EUserVisibility,
  type ITEUserWithTEIdPAuthSettings,
  type TAttributesAndValuesPair,
  type TFindManyTEUsersBody,
  type TTEUser,
  createUser as createTEUser,
} from '@timeedit/types';
import type { TAppDispatch } from '..';
import api from '../services/api.service';

export const MAX_RESULTS = 200;

type TUserState = IDefaultAPIState<TTEUser> & {
  isFirstUserSaveDisabled: boolean;
  isSecondUserSaveDisabled: boolean;
  // TODO: Figure out when ITEUserWithTEIdPAuthSettings is actually appropriate
  firstSelectedUser?: ITEUserWithTEIdPAuthSettings;
  // TODO: Figure out when ITEUserWithTEIdPAuthSettings is actually appropriate
  secondSelectedUser?: ITEUserWithTEIdPAuthSettings;
  selectedUserIds: string[];
  selectedUsers: TTEUser[];
  totalResults: number;
  importProgress: number;
  userActionFilter: TFindManyTEUsersBody;
  findUserPayload: TFindManyTEUsersBody;
  importError: { message: string } | null;
  totalUsers: number;
  shouldReload: boolean;
  apiActionState: {
    deactivateUsers?: { loading: boolean; error: string | null };
    activateUsers?: { loading: boolean; error: string | null };
  };
};

export const initialState: TUserState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  totalResults: 0,
  isFirstUserSaveDisabled: true,
  isSecondUserSaveDisabled: true,
  firstSelectedUser: undefined,
  secondSelectedUser: undefined,
  userActionFilter: {
    filter: {},
  },
  findUserPayload: {
    limit: MAX_RESULTS,
    page: 1,
    filter: {},
  },
  selectedUserIds: [],
  selectedUsers: [],
  importProgress: -1,
  importError: null,
  totalUsers: 0,
  shouldReload: false,
  apiActionState: {},
};

export interface IImportUser {
  id?: string;
  profileName?: string;
  email: string;
  username: string;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  userObjects: string[];
  standardOrganization: string | null | undefined;
  organizationsAndRoles: TAttributesAndValuesPair[];
  scopes: string[];
  appPermissions: string[];
  language: string | null | undefined;
  inactiveSince?: number | null;
  visibility: EUserVisibility;
  description: string | null | undefined;
  lockedForExternalAuthentication: boolean;
}

const DEFAULT_LIMIT = 50;

export type ReturnedUsers = {
  results: TTEUser[];
  count: number;
  page: number;
  totalPages: number;
  totalResults: number;
};

export const exportUsers = async (options: TFindManyTEUsersBody) => {
  return await api.post<TFindManyTEUsersBody, Blob>({
    endpoint: '/users/export/csv',
    responseType: 'blob',
    suppressNotification: true,
    data: createFindManyTEUsersBodyWithoutEmptyValues(options),
  });
};

export const fetchManyUsers = async (options: TFindManyTEUsersBody) => {
  return await api.post<TFindManyTEUsersBody, ReturnedUsers>({
    endpoint: '/users/find-many',
    suppressNotification: true,
    data: createFindManyTEUsersBodyWithoutEmptyValues({
      ...options,
      limit: options.limit || DEFAULT_LIMIT,
    }),
  });
};

export const fetchExportUsersReview = async (options: TFindManyTEUsersBody) => {
  return await api.post<TFindManyTEUsersBody, ReturnedUsers>({
    endpoint: '/users/export/review',
    suppressNotification: true,
    data: createFindManyTEUsersBodyWithoutEmptyValues({
      ...options,
      limit: options.limit || DEFAULT_LIMIT,
    }),
  });
};

function createFindManyTEUsersBodyWithoutEmptyValues(
  options: TFindManyTEUsersBody
): TFindManyTEUsersBody {
  return Object.entries(options).reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }

    return acc;
  }, {} as TFindManyTEUsersBody);
}

export const fetchMoreUsers = createAppAsyncThunk(
  'users/fetchMoreUsers',
  async (
    options: TFindManyTEUsersBody,
    { dispatch }
  ): Promise<ReturnedUsers> => {
    dispatch(fetchUsersRequest());
    const response = await fetchManyUsers(options);
    dispatch(fetchMoreUsersSuccess(response.results));
    return response;
  }
);

const slice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    fetchUsersRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchUsersSuccess: (state, { payload: users }) => {
      state.results = users;
      state.map = keyBy(users, 'id');
      finishedLoadingSuccess(state);
    },
    fetchMoreUsersSuccess: (state, { payload: users }) => {
      state.results = state.results.concat(users);
      state.map = merge({}, state.map, keyBy(users, 'id'));
      finishedLoadingSuccess(state);
    },
    fetchUsersByIdSuccess: (
      state,
      { payload }: PayloadAction<TFetchUserResult[]>
    ) => {
      for (const userResult of payload) {
        const { isLegacyUser, user } = userResult;

        if (isLegacyUser) {
          state.map[user.legacyId] = user;
        }

        state.map[user.id] = user;
      }

      finishedLoadingSuccess(state);
    },
    fetchUsersFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createUnsavedUser: (state) => {
      state.firstSelectedUser = addEntityToState(
        state,
        {
          id: 'new',
          firstName: 'Firstname',
          lastName: 'Lastname',
          scopes: [],
          appPermissions: [],
          organizationsAndRoles: [],
          userObjects: [],
          profileName: 'Default',
        },
        createTEUser
      ) as ITEUserWithTEIdPAuthSettings;
      finishedLoadingSuccess(state);
    },
    duplicateUser: (state, { payload }) => {
      state.firstSelectedUser = addEntityToState(
        state,
        {
          ...payload,
          id: 'new',
          lastName: payload.lastName + '-copy',
          profiles: payload.profiles?.map(({ id, legacyId, ...profile }) => ({
            id: `new-profile-${id}-${Date.now()}`,
            ...profile,
          })),
          history: [],
          logins: [],
        },
        createTEUser
      ) as ITEUserWithTEIdPAuthSettings;
      finishedLoadingSuccess(state);
    },
    changeUser: (state, { payload }) => {
      const { isFirstDrawer, key, value } = payload;
      const user = isFirstDrawer
        ? state.firstSelectedUser
        : state.secondSelectedUser;
      if (user) {
        user[key] = value;
      }
    },
    saveUserRequest: (state) => {
      isLoadingRequest(state);
    },
    saveUserSuccess: (
      state,
      {
        payload: { user, isFirstDrawer },
      }: PayloadAction<{ user: TTEUser; isFirstDrawer: boolean }>
    ) => {
      upsertEntity(state, user, createTEUser);
      if (isFirstDrawer) {
        state.firstSelectedUser = undefined;
      } else {
        state.secondSelectedUser = undefined;
      }

      state.map[user.id] = user;
      state.results = state.results.map((u) => (u.id === user.id ? user : u));
      state.selectedUsers = state.selectedUsers.map((u) =>
        u.id === user.id ? user : u
      );

      finishedLoadingSuccess(state);
    },
    saveUserFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteUserRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteUserSuccess: (state, { payload: { userId } }) => {
      deleteEntityFromState(userId, state);
      if (state.firstSelectedUser?.id === userId) {
        state.firstSelectedUser = undefined;
      } else {
        state.secondSelectedUser = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deleteUserFailure: (state) => {
      finishedLoadingFailure(state);
    },
    resetPasswordForUserRequest: (state) => {
      isLoadingRequest(state);
    },
    resetPasswordForUserSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    resetPasswordForUserFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setIsUserDisabled: (
      state,
      {
        payload: { isFirstDrawer, user },
      }: PayloadAction<{
        isFirstDrawer: boolean;
        user?: TTEUser;
      }>
    ) => {
      const property = isFirstDrawer
        ? 'isFirstUserSaveDisabled'
        : 'isSecondUserSaveDisabled';

      if (!user) {
        state[property] = true;
        return;
      }

      state[property] = checkSavedDisabled(user, state.map[user.id]);
    },
    setFirstSelectedUser: (state, { payload: user }) => {
      const prevUser = state.firstSelectedUser;
      state.firstSelectedUser = user;
      if (prevUser?.id === 'new') {
        state.results = state.results.filter((u: any) => u.id !== 'new');
        state.map.new = undefined;
      }
    },
    setSecondSelectedUser: (state, { payload: user }) => {
      state.secondSelectedUser = user;
    },
    setSelectedUserIds: (state, { payload: ids }: PayloadAction<string[]>) => {
      state.selectedUserIds = ids;
    },
    setSelectedUsers: (
      state: TUserState,
      { payload }: PayloadAction<TTEUser[]>
    ) => {
      state.selectedUsers = payload;
    },
    setIsImportingUsers: (state, { payload }) => {
      state.importProgress = payload;
    },
    setIsImportingUsersError: (state, { payload }) => {
      state.importError = payload;
    },
    setUserActionFilter: (
      state,
      { payload }: PayloadAction<TFindManyTEUsersBody>
    ) => {
      state.userActionFilter = payload;
    },
    setFindUserPayload: (
      state,
      { payload }: PayloadAction<TFindManyTEUsersBody>
    ) => {
      state.findUserPayload = payload;
    },
    fetchTotalUsersSuccess: (state, { payload }: PayloadAction<number>) => {
      state.totalUsers = payload;
    },
    triggerReload: (state) => {
      state.shouldReload = !state.shouldReload;
    },
    deactivateUserRequest: (state) => {
      state.apiActionState.deactivateUsers = {
        loading: true,
        error: null,
      };
    },
    deactivateUserSuccess: (state) => {
      if (state.apiActionState.deactivateUsers) {
        state.apiActionState.deactivateUsers.loading = false;
        state.apiActionState.deactivateUsers.error = null;
      }
    },
    deactivateUserFailure: (state, { payload }: PayloadAction<string>) => {
      if (state.apiActionState.deactivateUsers) {
        state.apiActionState.deactivateUsers.loading = false;
        state.apiActionState.deactivateUsers.error =
          payload || 'Error deactivating users';
      }
    },
    activateUserRequest: (state) => {
      state.apiActionState.activateUsers = {
        loading: true,
        error: null,
      };
    },
    activateUserSuccess: (state) => {
      if (state.apiActionState.activateUsers) {
        state.apiActionState.activateUsers.loading = false;
        state.apiActionState.activateUsers.error = null;
      }
    },
    activateUserFailure: (state, { payload }: PayloadAction<string>) => {
      if (state.apiActionState.activateUsers) {
        state.apiActionState.activateUsers.loading = false;
        state.apiActionState.activateUsers.error =
          payload || 'Error activating users';
      }
    },
  },
  selectors: {
    isFirstUserSaveDisabledSelector: (state) => state.isFirstUserSaveDisabled,
    isSecondUserSaveDisabledSelector: (state) => state.isSecondUserSaveDisabled,
    selectedUserIdsSelector: (state) => state.selectedUserIds,
  },
});

export default slice.reducer;

// Selectors
export const usersSelector = (state: TRootState) => state.users.results;
export const userSelector = (id?: string) => (state: TRootState) =>
  id ? state.users.map[id] : undefined;
export const usersMapSelector = (state: TRootState) => state.users.map;
export const firstSelectedUserSelector = (state: TRootState) =>
  state.users.firstSelectedUser;
export const secondSelectedUserSelector = (state: TRootState) =>
  state.users.secondSelectedUser;
export const selectedUsersSelector = (state: TRootState) =>
  state.users.selectedUsers;
export const importProgressSelector = (state: TRootState) =>
  state.users.importProgress;
export const importErrorSelector = (state: TRootState) =>
  state.users.importError;
export const userActionFilterSelector = (state: TRootState) =>
  state.users.userActionFilter;
export const totalUsersSelector = (state: TRootState) => state.users.totalUsers;
export const shouldReloadSelector = (state: TRootState) =>
  state.users.shouldReload;

export const deactivateUsersLoadingSelector = (state: TRootState) =>
  state.users.apiActionState.deactivateUsers?.loading;

export const activateUsersLoadingSelector = (state: TRootState) =>
  state.users.apiActionState.activateUsers?.loading;

// Actions
export const {
  fetchUsersRequest,
  fetchUsersSuccess,
  fetchMoreUsersSuccess,
  fetchUsersFailure,
  createUnsavedUser,
  deleteUserRequest,
  deleteUserFailure,
  deleteUserSuccess,
  saveUserRequest,
  saveUserSuccess,
  saveUserFailure,
  resetPasswordForUserRequest,
  resetPasswordForUserSuccess,
  resetPasswordForUserFailure,
  setFirstSelectedUser,
  setSecondSelectedUser,
  duplicateUser,
  setIsImportingUsers,
  setIsUserDisabled,
  setSelectedUserIds,
  setSelectedUsers,
  changeUser,
  fetchUsersByIdSuccess,
  setUserActionFilter,
  setFindUserPayload,
  setIsImportingUsersError,
  fetchTotalUsersSuccess,
  triggerReload,
  deactivateUserRequest,
  deactivateUserSuccess,
  deactivateUserFailure,
  activateUserRequest,
  activateUserSuccess,
  activateUserFailure,
} = slice.actions;

export const {
  isFirstUserSaveDisabledSelector,
  isSecondUserSaveDisabledSelector,
  selectedUserIdsSelector,
} = slice.selectors;

export const fetchUserById =
  (userId: string) => async (dispatch: TAppDispatch) => {
    if (!userId) {
      return;
    }

    dispatch(fetchUsersByIds([userId]));
  };

type TFetchUserResult = { user: TTEUser; isLegacyUser?: boolean };

export const fetchUsersByIds =
  (userIds: string[]) => async (dispatch: TAppDispatch) => {
    if (!userIds.length) {
      return;
    }

    try {
      dispatch(fetchUsersRequest());
      const userResultPromises: Promise<TFetchUserResult>[] = [];

      for (const userId of userIds) {
        const isLegacyUser = !validateObjectId(userId);
        const endpoint = !isLegacyUser
          ? `/users/${userId}`
          : `/users/legacy/${userId}`;

        userResultPromises.push(
          api
            .get<any, TTEUser>({ endpoint, suppressNotification: true })
            .then((user) => ({ user, isLegacyUser }))
        );
      }

      const results = (await Promise.allSettled(userResultPromises))
        .filter((promise) => promise.status === 'fulfilled')
        .map((promise) => promise.value);

      dispatch(fetchUsersByIdSuccess(results));
    } catch (e) {
      dispatch(fetchUsersFailure());
    }
  };

export const saveUser = createAppAsyncThunk<
  void,
  { user: TTEUser; isFirstDrawer: boolean }
>('users/saveUser', async ({ user, isFirstDrawer }, { dispatch }) => {
  try {
    dispatch(saveUserRequest());

    const {
      createdBy: _createdBy,
      createdByDisplayName: _createdByDisplayName,
      ...userWithoutReadOnly
    } = user;
    const userPayload = {
      ...userWithoutReadOnly,
      profiles: user.profiles?.map((profile) => {
        if (!profile.id.includes('new-profile')) {
          return profile;
        }

        const { id, ...rest } = profile;
        return rest;
      }),
    };

    if (user.id === 'new') {
      // Create a new one
      const { legacyId, ...rest } = userPayload;
      const preppedUser = prepEntityForAPICall(rest, true, true);
      const savedUser = await api.post({
        endpoint: `/users`,
        data: preppedUser,
        successMessage: 'Successfully created user',
      });
      dispatch(saveUserSuccess({ user: savedUser, isFirstDrawer }));
      dispatch(deleteUserSuccess({ userId: 'new' }));
      dispatch(fetchTotalUsers());
      dispatch(triggerReload());
    } else {
      const preppedUser = prepEntityForAPICall(userPayload, true, true);
      const updatedUser = await api.patch({
        endpoint: `/users/${user.id}`,
        data: preppedUser,
      });
      dispatch(triggerReload());
      dispatch(saveUserSuccess({ user: updatedUser, isFirstDrawer }));
    }
  } catch (error) {
    dispatch(saveUserFailure());
    console.error(error);
  }
});

export const deleteUser =
  (userId: string) => async (dispatch: TAppDispatch) => {
    try {
      dispatch(deleteUserRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (userId !== 'new')
        await api.delete({
          endpoint: `/users/${userId}`,
        });
      dispatch(deleteUserSuccess({ userId }));
      dispatch(fetchTotalUsers());
      dispatch(triggerReload());
    } catch (e) {
      dispatch(deleteUserFailure());
      console.error(e);
    }
  };

export const resetPasswordForUser =
  (userId?: string) => async (dispatch: TAppDispatch) => {
    try {
      if (userId) {
        dispatch(resetPasswordForUserRequest());
        await api.post({
          endpoint: `/users/${userId}/reset-password`,
          successMessage:
            'An email with a link to reset their password has been emailed to the user',
        });
        dispatch(resetPasswordForUserSuccess());
      }
    } catch (e) {
      dispatch(resetPasswordForUserFailure());
      console.error(e);
    }
  };

export const importUsers =
  (
    authConfigId: string,
    users: IImportUser[],
    options?: { hasInactiveSince?: boolean }
  ): TAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const handleTabClose = registerReloadWarning();
    dispatch(setIsImportingUsersError(null));
    try {
      dispatch(setIsImportingUsers(0));
      // Group rows by email so that all profile rows for the same user are kept
      // together. Chunking the flat row array directly can split a user's profile
      // rows across two requests, causing the second request to overwrite the
      // user's profiles with an incomplete set.
      const userGroups = Object.values(
        groupBy(users, (u) => u.email.trim().toLowerCase())
      );
      const chunks: IImportUser[][] = [];
      let current: IImportUser[] = [];
      for (const group of userGroups) {
        if (current.length > 0 && current.length + group.length > 50) {
          chunks.push(current);
          current = [];
        }
        current.push(...group);
      }
      if (current.length > 0) chunks.push(current);
      const chunkUsers = chunks;
      let hasError = false;
      for (let i = 0; i < chunkUsers.length; i++) {
        try {
          await api.post({
            endpoint: `/users/import`,
            data: {
              authConfigId,
              users: chunkUsers[i],
              hasInactiveSince: options?.hasInactiveSince,
            },
            suppressNotification: true,
          });
          dispatch(
            setIsImportingUsers(Math.floor(((i + 1) * 100) / chunkUsers.length))
          );
        } catch (e) {
          console.error('errors_users/import', e);
          dispatch(
            setIsImportingUsersError({
              message: get(e, 'response.data.message', 'Error importing users'),
            })
          );
          hasError = true;
          break;
        }
      }
      if (!hasError) {
        const { findUserPayload } = getState().users;
        const response = await fetchManyUsers({
          ...findUserPayload,
          limit: MAX_RESULTS,
          page: 1,
        });
        dispatch(fetchUsersSuccess(response.results));
        dispatch(setIsImportingUsers(100));
      }
    } catch (e) {
      console.error(e);
    } finally {
      unregisterReloadWarning(handleTabClose);
    }
  };

export const fetchTotalUsers = createAppAsyncThunk(
  'users/fetchTotalUsers',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchUsersRequest());
      const { totalResults } = await fetchManyUsers({
        filter: {},
        page: 1,
        limit: 1,
      });
      dispatch(fetchTotalUsersSuccess(totalResults));
    } catch (e) {
      dispatch(fetchUsersFailure());
    }
  }
);

export const checkUserSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { users } = getState();
    return isFirstDrawer
      ? users.isFirstUserSaveDisabled
      : users.isSecondUserSaveDisabled;
  };

export const checkFirstUserExists = (): TAppThunk<boolean> => (_, getState) =>
  !!getState().users.firstSelectedUser;

export const deactivateUser = createAppAsyncThunk<void>(
  'users/deactivateUser',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(deactivateUserRequest());
      const userActionFilter = getState().users.userActionFilter;
      const response = await api.patch({
        endpoint: `/users/deactivate`,
        data: userActionFilter,
        suppressNotification: true,
      });

      if (response?.success === 0) {
        message.warning('No users were deactivated');
      } else {
        message.success(
          `${response?.success} user(s) have been deactivated successfully`
        );
        dispatch(fetchTotalUsers());
        dispatch(triggerReload());
      }
      dispatch(deactivateUserSuccess());
    } catch (error) {
      const errorMessage =
        (error instanceof AxiosError
          ? error?.response?.data?.message
          : undefined) || 'An unexpected error occurred';

      const errorTitle =
        (error instanceof AxiosError ? error?.message : undefined) ||
        'Unknown error occurred';

      errorNotification(errorTitle, [errorMessage]);
      dispatch(deactivateUserFailure(errorMessage));
      console.error(error);
    }
  }
);

export const activateUser = createAppAsyncThunk<void>(
  'users/activateUser',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(activateUserRequest());
      const userActionFilter = getState().users.userActionFilter;
      const response = await api.patch({
        endpoint: `/users/activate`,
        data: userActionFilter,
        suppressNotification: true,
      });

      if (response?.success === 0) {
        message.warning('No users were activated');
      } else {
        message.success(
          `${response?.success} user(s) have been activated successfully`
        );
        dispatch(fetchTotalUsers());
        dispatch(triggerReload());
      }
      dispatch(activateUserSuccess());
    } catch (error) {
      const errorMessage =
        (error instanceof AxiosError
          ? error?.response?.data?.message
          : undefined) || 'An unexpected error occurred';

      const errorTitle =
        (error instanceof AxiosError ? error?.message : undefined) ||
        'Unknown error occurred';

      errorNotification(errorTitle, [errorMessage]);
      dispatch(activateUserFailure(errorMessage));
      console.error(error);
    }
  }
);

startAppListening({
  predicate: (_, currentState, previousState) =>
    currentState.users.firstSelectedUser?.id !==
      previousState.users.firstSelectedUser?.id ||
    currentState.users.secondSelectedUser?.id !==
      previousState.users.secondSelectedUser?.id,
  effect: (_, { dispatch, getState }) => {
    const { users } = getState();

    dispatch(
      setSelectedUserIds(
        compact([users.firstSelectedUser?.id, users.secondSelectedUser?.id])
      )
    );
  },
});

type SelectedUserKey = 'firstSelectedUser' | 'secondSelectedUser';

const selectedUserPredicate =
  (key: SelectedUserKey): StartAppListeningPredicate =>
  (_, currentState, previousState) =>
    currentState.users[key] !== previousState.users[key];

const selectedUserEffect =
  (key: SelectedUserKey): StartAppListenerEffect =>
  (_, { dispatch, getState }) => {
    dispatch(
      setIsUserDisabled({
        isFirstDrawer: key === 'firstSelectedUser',
        user: getState().users[key],
      })
    );
  };

startAppListening({
  predicate: selectedUserPredicate('firstSelectedUser'),
  effect: selectedUserEffect('firstSelectedUser'),
});

startAppListening({
  predicate: selectedUserPredicate('secondSelectedUser'),
  effect: selectedUserEffect('secondSelectedUser'),
});
